import React from 'react';
import ReactSVG from 'react-svg';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import * as actions from '~/store/actions';
import { ChannelTypeEnum } from '~/enums/conversations';
import { Conversation } from '~/interfaces/conversations';
import { useNavigation } from '~/hooks';
import { dayjs } from '~/dependencies';
import { log } from '~/helpers';
import { getChannelIconById } from '~/helpers/conversation';
import RoundedCheckbox from '../RoundedCheckbox';
import AssignedAgentSelector from '../AssignedAgentSelector';
import TTRBadge from '../TTRBadge';
import {
  ConversationItem,
  Details,
  Avatar,
  Headerline,
  Caption,
  ContactName,
  RightSide,
  Counter,
  MessagePreview,
  Badge,
} from './styles';

type AvatarContactChannelProps = {
  contactName: string;
  channelType: ChannelTypeEnum;
};

function LastMessageTime({ dataTime }: { dataTime: string }) {
  return <span>{dataTime}</span>;
}

function LastMessagePreview({ message }: { message: string }) {
  return <MessagePreview>{message}</MessagePreview>;
}

function UnreadMessageCount({ count }: { count: number }) {
  return <Counter overlap="circular" color="primary" badgeContent={count} />;
}

function AvatarContactChannel({
  contactName,
  channelType,
}: AvatarContactChannelProps) {
  const initials = contactName
    ?.trim()
    .split(' ')
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join('');

  return (
    <Badge
      data-test-id={`avatar-contact-channel-${channelType}`}
      overlap="circular"
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      badgeContent={<ReactSVG src={getChannelIconById(channelType)} />}>
      <Avatar>{initials}</Avatar>
    </Badge>
  );
}

function formatConversationDate(date: string, userTz: string) {
  const isSameDay = dayjs()
    .tz(userTz)
    .isSame(date, 'day');

  return dayjs(date)
    .tz(userTz)
    .format(isSameDay ? 'HH:mm' : 'MMM D');
}

type RowProps = {
  index: number;
  item: Conversation;
};

// @ts-ignore
export default function Row({ item, index }: RowProps) {
  // HOOKS
  const dispatch = useDispatch();
  const navigate = useNavigation();

  // SELECTORS
  const openSelection = useSelector(
    (state: RootStateOrAny) => state.conversation.openSelection
  );
  const userTz = useSelector(
    (state: RootStateOrAny) => state.auth.userTimeZone
  );

  // CONST's
  const {
    id,
    assignedName,
    ttr,
    unread,
    messages,
    channelType,
    selected,
    contactName,
    contactNumber,
    expiresAt,
  } = item;

  const lastMessage = messages[messages.length - 1];
  const creationDate = formatConversationDate(lastMessage?.createdAt, userTz);

  const handleSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    item.selected = event.target.checked;
    dispatch(actions.updateConversation(item));
  };

  const handleClickConversation = () => {
    log.info('Clicked conversation: ', item);
    dispatch(actions.setConversationLastReadIndex(index));
    navigate.chatView(item);
  };

  return (
    <ConversationItem key={id} id={id} isUnread={unread > 0}>
      {openSelection ? (
        <RoundedCheckbox checked={selected} onChange={handleSelection} />
      ) : null}
      <AvatarContactChannel
        contactName={contactName}
        channelType={channelType}
      />
      <Details onClick={handleClickConversation}>
        <Headerline>
          <RightSide>
            {expiresAt ? <TTRBadge expireIn={ttr} /> : null}
            <ContactName title={contactNumber}>{contactName}</ContactName>
            <AssignedAgentSelector
              conversationId={id}
              agentName={assignedName}
            />
          </RightSide>
          <LastMessageTime dataTime={creationDate} />
        </Headerline>
        <Caption>
          <LastMessagePreview message={lastMessage?.message} />
          {unread > 0 ? <UnreadMessageCount count={unread} /> : null}
        </Caption>
      </Details>
    </ConversationItem>
  );
}
